/**
 * Button Label Mapping Configuration
 * 
 * Maps technical button element names to human-readable labels
 * for display in the admin panel analytics.
 * 
 * Created: November 19, 2025
 * Purpose: Session fragmentation fix + Button tracking system (Task 3.1)
 */

export const buttonLabelMap: Record<string, string> = {
  // ============================================================================
  // TABLE OF CONTENTS (TOC) PAGE BUTTONS
  // ============================================================================
  
  // Chapter Navigation Buttons
  "toc_chapter_1_navigation": "Chapter 1: The Still Pond",
  "toc_chapter_2_navigation": "Chapter 2: The Colorful Chameleon",
  "toc_chapter_3_navigation": "Chapter 3: The Brave Bridge",
  "toc_chapter_4_navigation": "Chapter 4: The Kindness Meadow",
  "toc_chapter_5_navigation": "Chapter 5: The Gratitude Garden",
  "toc_chapter_6_navigation": "Chapter 6: The Sharing Tree",
  
  // TOC Action Buttons
  "toc_start_here_button": "Start Here (Introduction)",
  "toc_bookmark_save_button": "Save Bookmark",
  "toc_bookmark_clear_button": "Clear Bookmark",
  "toc_get_full_access_button": "Get Full Access",
  
  // TOC Collapsible Elements
  "toc_guide_collapsible": "How to Read This Book (Guide)",
  "toc_editor_note_collapsible": "Message to Our Readers",
  
  // ============================================================================
  // CHAPTER READING PAGE BUTTONS (All Chapters 1-6)
  // ============================================================================
  
  // Universal Navigation Buttons
  "home_button": "Home Button",
  "back_to_contents_button": "Back to Contents",
  "activities_button": "Activities Button",
  
  // Spread Navigation (Arrows)
  "spread_nav: previous": "Previous Spread ←",
  "spread_nav: next": "Next Spread →",
  
  // Chapter Navigation (Arrows)
  "chapter_nav: previous": "← Previous Chapter",
  "chapter_nav: next": "Next Chapter →",
  
  // ============================================================================
  // INTERACTIVE CUES (Mindfulness Activities)
  // ============================================================================
  
  // Chapter 1: The Still Pond
  "cue-Golden Leaf": "🍃 Golden Leaf (Mindfulness)",
  "cue_click: Golden Leaf": "🍃 Golden Leaf (Mindfulness)",
  
  // Chapter 2: The Colorful Chameleon
  "cue-Rainbow Tail": "🌈 Rainbow Tail (Emotions)",
  "cue_click: Rainbow Tail": "🌈 Rainbow Tail (Emotions)",
  
  // Chapter 3: The Brave Bridge
  "cue-Bravery Badge": "🛡️ Bravery Badge (Courage)",
  "cue_click: Bravery Badge": "🛡️ Bravery Badge (Courage)",
  
  // Chapter 4: The Kindness Meadow
  "cue-Sparkling Petal": "✨ Sparkling Petal (Kindness)",
  "cue_click: Sparkling Petal": "✨ Sparkling Petal (Kindness)",
  
  // Chapter 5: The Gratitude Garden
  "cue-Gratitude Leaf": "💖 Gratitude Leaf (Gratitude)",
  "cue_click: Gratitude Leaf": "💖 Gratitude Leaf (Gratitude)",
  
  // Chapter 6: The Sharing Tree
  "cue-Dew Cup": "☕ Dew Cup (Sharing)",
  "cue_click: Dew Cup": "☕ Dew Cup (Sharing)",
  
  // ============================================================================
  // ACTIVITY PAGE PRINT BUTTONS
  // ============================================================================
  
  // Chapter-Specific Print Buttons
  "print-chapter_1_activities": "Print Chapter 1 Activities",
  "print-chapter_2_activities": "Print Chapter 2 Activities",
  "print-chapter_3_activities": "Print Chapter 3 Activities",
  "print-chapter_4_activities": "Print Chapter 4 Activities",
  "print-chapter_5_activities": "Print Chapter 5 Activities",
  "print-chapter_6_activities": "Print Chapter 6 Activities",
  
  // Print All Resources
  "print-all_resources_button": "Print All Resources",
  "print_all_resources_button": "Print All Resources",
  
  // ============================================================================
  // GENERIC/FALLBACK BUTTONS
  // ============================================================================
  
  // Common Elements
  "button": "Generic Button",
  "link": "Generic Link",
  "nav": "Navigation Element",
};

/**
 * Get human-readable label for a technical button name
 * 
 * @param technicalName - The technical element name from tracking (e.g., "toc_chapter_1_navigation")
 * @returns Human-readable label (e.g., "Chapter 1: The Still Pond") or original name if not mapped
 */
export function getButtonLabel(technicalName: string): string {
  // Try direct lookup first
  if (buttonLabelMap[technicalName]) {
    return buttonLabelMap[technicalName];
  }
  
  // Try to extract cue name from cue-interaction elements
  if (technicalName.startsWith('cue-')) {
    const cueName = technicalName.substring(4);
    const cueMapping = buttonLabelMap[`cue-${cueName}`];
    if (cueMapping) return cueMapping;
  }
  
  // Try to extract print target from print elements
  if (technicalName.startsWith('print-')) {
    const printTarget = technicalName.substring(6);
    const printMapping = buttonLabelMap[`print-${printTarget}`];
    if (printMapping) return printMapping;
  }
  
  // Fallback: Return original technical name with basic formatting
  return technicalName
    .replace(/_/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase());
}

/**
 * Get category for a button (for grouping in admin panel)
 * 
 * @param technicalName - The technical element name
 * @returns Category string for grouping
 */
export function getButtonCategory(technicalName: string): string {
  if (technicalName.startsWith('toc_chapter')) return 'TOC Navigation';
  if (technicalName.startsWith('toc_')) return 'TOC Actions';
  if (technicalName.startsWith('cue-') || technicalName.includes('cue_click')) return 'Interactive Cues';
  if (technicalName.startsWith('print-')) return 'Print Actions';
  if (technicalName.includes('nav:') || technicalName.includes('chapter_nav') || technicalName.includes('spread_nav')) return 'Chapter Navigation';
  if (technicalName === 'home_button' || technicalName === 'back_to_contents_button') return 'Main Navigation';
  if (technicalName === 'activities_button') return 'Activity Navigation';
  
  return 'Other';
}

/**
 * Get icon for a button category
 * 
 * @param category - The button category
 * @returns Icon string (emoji or unicode)
 */
export function getCategoryIcon(category: string): string {
  const iconMap: Record<string, string> = {
    'TOC Navigation': '📚',
    'TOC Actions': '⚡',
    'Interactive Cues': '✨',
    'Print Actions': '🖨️',
    'Chapter Navigation': '➡️',
    'Main Navigation': '🏠',
    'Activity Navigation': '🎯',
    'Other': '🔘'
  };
  
  return iconMap[category] || '🔘';
}
